/**
 * TEMPORARY — frontend-only duration/title for timeline bulk link import.
 * Not part of core composition backend. Set ENABLE_BULK_LINK_FRONTEND_RESOLVE to false
 * or stop importing this module to disable.
 */
import { classifyVideoUrl, isRemoteShareLink, sharePlaybackUrl } from './videoPlayback';

/** Flip to false to disable all bulk link duration probing. */
export const ENABLE_BULK_LINK_FRONTEND_RESOLVE = true;

function parseYoutubeId(url) {
  const m = (url || '').match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/i,
  );
  return m?.[1] || null;
}

let ytApiPromise = null;

function ensureYouTubeIframeApi() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.YT?.Player) return Promise.resolve();
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve();
    };
    if (document.getElementById('yt-iframe-api-bulk')) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'yt-iframe-api-bulk';
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => reject(new Error('YouTube API failed to load'));
    document.head.appendChild(script);
  });
  return ytApiPromise;
}

function probeHtmlVideoDuration(src, timeoutMs = 90000) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    let settled = false;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      video.remove();
      fn(value);
    };

    const timer = setTimeout(
      () => finish(reject, new Error('Timed out loading video metadata')),
      timeoutMs,
    );

    video.onloadedmetadata = () => {
      const d = Number(video.duration);
      if (d > 0.05 && Number.isFinite(d)) finish(resolve, d);
      else finish(reject, new Error('No duration in video metadata'));
    };
    video.onerror = () => finish(reject, new Error('Could not load video metadata'));
    video.src = src;
  });
}

function getYoutubeDurationBrowser(url) {
  const id = parseYoutubeId(url);
  if (!id) return Promise.reject(new Error('Invalid YouTube URL'));

  return ensureYouTubeIframeApi().then(
    () => new Promise((resolve, reject) => {
      const host = document.createElement('div');
      host.setAttribute('aria-hidden', 'true');
      host.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;top:0';
      document.body.appendChild(host);

      let player = null;
      let settled = false;
      const timer = setTimeout(() => cleanup(new Error('YouTube duration timeout')), 45000);

      const cleanup = (err, duration) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try {
          player?.destroy?.();
        } catch {
          /* ignore */
        }
        host.remove();
        if (err) reject(err);
        else resolve(duration);
      };

      player = new window.YT.Player(host, {
        videoId: id,
        events: {
          onReady: (e) => {
            const d = Number(e.target.getDuration());
            if (d > 0.05) cleanup(null, d);
            else cleanup(new Error('YouTube returned no duration'));
          },
          onError: () => cleanup(new Error('YouTube player error')),
        },
      });
    }),
  );
}

/**
 * Resolve one URL for bulk import (original share URL stored; full-length end time).
 */
export async function resolveBulkImportUrl(api, originalUrl, token) {
  if (!ENABLE_BULK_LINK_FRONTEND_RESOLVE) {
    throw new Error('Bulk link frontend resolve is disabled (bulkLinkResolve.js)');
  }

  const url = (originalUrl || '').trim();
  if (!url) throw new Error('Empty URL');

  const { kind } = classifyVideoUrl(url);
  let title = '';
  let duration = 0;

  if (isRemoteShareLink(url) || kind === 'embed') {
    const res = await api.post(
      '/dreamface/resolve-preview',
      { url },
      token ? { headers: { Authorization: `Bearer ${token}` } } : {},
    );
    title = (res.data?.work_name || res.data?.title || '').trim();
    duration = Number(res.data?.duration || 0);
    const stream = res.data?.stream_url;

    if (duration <= 0.05 && stream) {
      try {
        duration = await probeHtmlVideoDuration(stream);
      } catch {
        duration = await probeHtmlVideoDuration(sharePlaybackUrl(url));
      }
    }
  } else if (kind === 'youtube') {
    duration = await getYoutubeDurationBrowser(url);
  } else if (kind === 'file') {
    duration = await probeHtmlVideoDuration(url);
  } else {
    throw new Error('Unsupported URL type for bulk import');
  }

  if (!(duration > 0.05)) {
    throw new Error('Could not read video duration');
  }

  return {
    url,
    title: title || 'Import',
    duration,
  };
}

/** Resolve many URLs in order (sequential). */
export async function resolveBulkImportUrls(api, urls, token, { onItemError } = {}) {
  const resolved = [];
  for (let i = 0; i < urls.length; i += 1) {
    try {
      const item = await resolveBulkImportUrl(api, urls[i], token);
      resolved.push({
        ...item,
        title: item.title === 'Import' ? `Import ${i + 1}` : item.title,
      });
    } catch (err) {
      onItemError?.(i, urls[i], err);
    }
  }
  return resolved;
}
